import { useEffect, useMemo } from 'react';

import { useAutoNavigate } from '@betterment/js-runtime';
import { EchoNavigate, EchoSlot, type EchoSubmitFn, type EchoView, useEcho } from '@betterment/shared-components';
import { useLoaderData } from 'react-router-dom';

import { assertValidView } from './assertValidView';
import { getMeta, setMeta } from './meta';

import { Notifications } from '../../notifications/Notifications';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import FullPageLoader from '../../Shared/components/FullPageLoader';

function EchoView({ view: unknownView, onSubmit }: { view: EchoView; onSubmit: EchoSubmitFn }) {
  const [layout, view] = useMemo(() => assertValidView(unknownView), [unknownView]);

  useEffect(() => {
    document.body.className = view.props.bodyClassName;
    document.title = view.props.title;
    setMeta('csrf-token', view.props.csrfToken);

    const descriptionWas = getMeta('description')?.content;
    setMeta('description', view.props.description);
    return () => setMeta('description', descriptionWas);
  }, [view]);

  if (layout === 'application') {
    return (
      <WithLayout parts={{ sidebar: true, footer: true, globalNotice: true }}>
        <Notifications css={{ paddingTop: 'space.400' }} />
        <EchoSlot slot={view.slots.main} onSubmit={onSubmit} />
      </WithLayout>
    );
  }

  return (
    <WithLayout parts={{ sidebar: false, footer: false, globalNotice: false }}>
      <div className={view.props.siteLayoutClassName}>
        <EchoSlot slot={view.slots.header} onSubmit={onSubmit} />
        <Notifications css={{ paddingY: 'space.400' }} />
        <EchoSlot className="u-flexGrowBasisAuto" slot={view.slots.main} onSubmit={onSubmit} />
        <EchoSlot slot={view.slots.footer} onSubmit={onSubmit} />
      </div>

      <EchoSlot slot={view.slots.modals} onSubmit={onSubmit} />
    </WithLayout>
  );
}

export function EchoRoute() {
  const { echo, isPreview, onSubmit } = useEcho(useLoaderData());

  const autoNavigate = useAutoNavigate();

  useEffect(() => {
    document.body.toggleAttribute('data-echo-preview', isPreview);
    return () => document.body.removeAttribute('data-echo-preview');
  }, [isPreview]);

  useEffect(() => {
    const onTypedRedirect = (event: HTMLElementEventMap['typed:redirect']) => {
      event.preventDefault();
      void autoNavigate(event.detail.url);
    };

    document.body.addEventListener('typed:redirect', onTypedRedirect);
    return () => document.body.removeEventListener('typed:redirect', onTypedRedirect);
  }, [autoNavigate]);

  if (echo.type === 'navigation') {
    return <EchoNavigate echo={echo} fallback={<FullPageLoader />} />;
  }

  return <EchoView view={echo} onSubmit={onSubmit} />;
}
